import { Button, CompanyLogo, LanguageSwitcher } from '@/components/atoms';
import { enUS, id } from 'date-fns/locale';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink, useHistory, useLocation } from 'react-router-dom';

import Avatar from '@/images/avatar/avatar.png';
import { ACTION_AUTH } from '@/redux/actions';
import PATH from '@/routes/path';
import { HELPERS } from '@/utils';
import { setDefaultOptions } from 'date-fns';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { HeaderButtonProfile } from '../Header/components/HeaderButtonProfile';

const locales = { en: enUS, id };

const useOutsideClick = (ref, callback) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target) || !document.contains(event.target)) {
        return;
      }
      callback(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, callback]);
};

const NewNavbar = ({ enableBackgroundTransition = true, enableDynamicColors = true }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const { loggedIn, profile } = useSelector((state) => state.auth);

  const client = useSelector((state) => state.client);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleEventGa = (link) => {
    if (link.to === PATH.LEARNING_PROGRAM) {
      HELPERS.sendEventGA('click_learning_program_menu', {
        page_source: 'Header',
      });
    } else if ([PATH.CATALOG, PATH.CATALOG_COURSE].includes(link.to)) {
      HELPERS.sendEventGA('click_explore_courses_menu', {
        page_source: 'Header',
      });
    }
  };

  useOutsideClick(menuRef, () => {
    setIsOpen(false);
  });

  const [opacity, setOpacity] = useState(enableBackgroundTransition ? 0 : 1);

  const handleScroll = useCallback(() => {
    if (!enableBackgroundTransition) {
      if (opacity !== 1) setOpacity(1);
      return;
    }
    const scrollPosition = window.scrollY;
    const windowHeight = window.innerHeight;
    const maxOpacityPercentage = 0.5;
    const maxScrollForOpacityEffect = windowHeight * maxOpacityPercentage;

    if (scrollPosition <= maxScrollForOpacityEffect) {
      setOpacity(scrollPosition / maxScrollForOpacityEffect);
    } else {
      setOpacity(1);
    }
  }, [enableBackgroundTransition, opacity]);

  useEffect(() => {
    if (enableBackgroundTransition) {
      window.addEventListener('scroll', handleScroll);
      handleScroll();
      return () => window.removeEventListener('scroll', handleScroll);
    } else {
      setOpacity(1);
      window.removeEventListener('scroll', handleScroll);
    }
  }, [enableBackgroundTransition, handleScroll]);

  const LINKS = [
    {
      to: '/learning-program',
      text: 'learning_program',
    },
    {
      to: '/event',
      text: 'event',
    },
    {
      to: '/catalog',
      text: 'explore_module',
    },
    {
      to: '/subscription-package',
      text: 'subscription_package',
    },
    {
      to: {
        pathname: `https://wa.me/${HELPERS.parsePhone(client.payload?.contact_phone)}`,
        target: '_blank',
      },
      text: 'mentoring',
      target: '_blank',
    },
    {
      to: '/article',
      text: 'articles_news',
    },

    // {
    //   to: '/repositori',
    //   text: 'repository',
    // },
  ];
  const { t } = useTranslation();

  const getLinkColor = useCallback(() => {
    if (!enableDynamicColors) {
      return '#2F1D4E';
    }
    if (opacity === 0) return 'white';
    if (opacity === 1) return '#2F1D4E';
    const r = Math.round(47 + (255 - 47) * (1 - opacity));
    const g = Math.round(29 + (255 - 29) * (1 - opacity));
    const b = Math.round(78 + (255 - 78) * (1 - opacity));
    return `rgb(${r}, ${g}, ${b})`;
  }, [opacity, enableDynamicColors]);

  const dynamicColor = getLinkColor();
  const activeColor = '#8553d8';

  const [activeLanguage, setActiveLanguage] = useState(i18n.language);

  const handleSwitchLanguage = (lang) => {
    setDefaultOptions({ locale: locales[lang] });
    i18n.changeLanguage(lang);
  };

  const handleLogout = () => {
    dispatch(ACTION_AUTH.logout());
    HELPERS.GA.reset();
    history.push(PATH.HOME);
  };

  useEffect(() => setActiveLanguage(i18n.language), [i18n.language]);

  return (
    <div className="relative overflow-hidden">
      <nav
        className="fixed w-full px-6 lg:px-8 top-0 z-50 mx-auto transition-all duration-300"
        style={{
          backgroundColor: `rgba(255, 255, 255, ${opacity})`,
          boxShadow:
            opacity === 1 ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : 'none',
          padding: opacity === 1 ? '0.75rem 0' : '1.25rem 0',
        }}
        aria-label="Global"
      >
        <div className="container h-full mx-auto">
          <div className="flex justify-between w-full h-full">
            {/* Logo Section */}
            <div className="flex items-center flex-1 md:space-x-8 xl:space-x-12">
              <CompanyLogo link opacity={opacity || 0} />
            </div>

            {/* Navigation Links */}
            <div className="hidden lg:flex items-center gap-x-4 px-2">
              {LINKS.map((link, index) => (
                <NavLink
                  type="button"
                  key={index}
                  to={link.to}
                  exact
                  onClick={(e) => handleEventGa(link)}
                  target={link.target || '_self'}
                  className="py-2 px-3 text-sm rounded-md transition-all duration-200 hover:bg-opacity-10 hover:bg-gray-500 whitespace-nowrap"
                  style={{
                    color: dynamicColor,
                    fontWeight: '500',
                  }}
                  activeStyle={{
                    color: activeColor,
                    fontWeight: '600',
                  }}
                >
                  {t(link.text)}
                </NavLink>
              ))}
            </div>

            {/* Right Section: Auth & Language */}
            <div className="hidden md:flex space-x-2">
              {loggedIn ? (
                <div className="flex items-center space-x-3">
                  <HeaderButtonProfile authProfile={profile} desktopMode textColor={dynamicColor} />
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <Button
                    variant={Button.VARIANTS.GRAY_OUTLINE}
                    size={Button.SIZES.MEDIUM}
                    style={{
                      color: dynamicColor,
                      borderColor: dynamicColor,
                      minWidth: '100px',
                      height: '40px',
                    }}
                    className="text-sm font-medium hover:bg-opacity-10 hover:bg-gray-500 whitespace-nowrap"
                    onClick={(e) => {
                      HELPERS.sendEventGA('click_login_menu', {
                        page_source: 'Header',
                      });

                      history.push(PATH.AUTH_LOGIN);
                    }}
                  >
                    {t('auth_login')}
                  </Button>
                  <Button
                    variant={Button.VARIANTS.SECONDARY}
                    size={Button.SIZES.MEDIUM}
                    onClick={(e) => {
                      HELPERS.sendEventGA('click_register_menu', {
                        page_source: 'Header',
                      });
                      history.push(PATH.AUTH_REGISTER);
                    }}
                    className="text-sm font-medium whitespace-nowrap"
                    style={{
                      minWidth: '100px',
                      height: '40px',
                    }}
                  >
                    {t('auth_register')}
                  </Button>
                  <div className="flex items-center ml-2" style={{ color: dynamicColor }}>
                    <LanguageSwitcher />
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="flex lg:hidden">
              <button
                type="button"
                className="inline-flex items-center justify-center p-2 rounded-md"
                style={{ color: dynamicColor }}
                onClick={toggleMenu}
              >
                <span className="sr-only">Open main menu</span>
                {isOpen ? (
                  <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>
      {isOpen && (
        <div
          ref={menuRef}
          className="fixed z-30 top-20 inset-x-0 p-2 transition transform origin-top-right lg:hidden"
        >
          <div className="rounded-lg p-2 pb-4 shadow-md bg-white ring-1 ring-black ring-opacity-5 overflow-hidden">
            <div className="font-bold text-white-app pt-2 pb-3 space-y-1">
              {LINKS.map((link, index) => (
                <NavLink
                  key={index}
                  type="button"
                  to={link.to}
                  exact
                  target={link.target ?? '_self'}
                  onClick={(e) => {
                    toggleMenu(e);
                    handleEventGa(link);
                  }}
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50"
                  style={{
                    color: '#2F1D4E',
                    backgroundColor: 'transparent',
                  }}
                  activeStyle={{
                    color: activeColor,
                    fontWeight: '600',
                    backgroundColor: 'rgba(133, 83, 216, 0.1)',
                  }}
                >
                  {t(link.text)}
                </NavLink>
              ))}
            </div>
            <div className="px-5 py-4 w-full flex flex-col">
              {loggedIn && (
                <>
                  <div className="flex items-center gap-2 py-4">
                    <img
                      className="object-cover object-top w-12 h-12 rounded-full outline outline-4 outline-gray-200"
                      src={profile?.avatar_url || Avatar}
                      alt="Avatar"
                    />
                    <div>
                      <div className="font-semibold">{profile?.name}</div>
                      <div className="font-light text-gray-600">{profile?.email}</div>
                    </div>
                  </div>
                  <hr />
                  <Link
                    className="inline-block w-full py-3 hover:bg-gray-100"
                    to={PATH.PROFILE}
                    onClick={toggleMenu}
                  >
                    {t('see_profile')}
                  </Link>
                </>
              )}
              <hr />
              <div className="space-y-3 pt-2">
                <div>Bahasa</div>
                <div className="space-y-2">
                  {LanguageSwitcher.languageOptions.map(({ value, label, description }) => (
                    <div key={value} className="flex items-center gap-1.5" htmlFor={`#${label}`}>
                      <input
                        checked={activeLanguage === value}
                        id={label}
                        type="radio"
                        name="language"
                        value={value}
                        className="w-4 h-4 ring-secondary"
                        onChange={() => handleSwitchLanguage(value)}
                      />
                      <label className="flex items-center gap-1 cursor-pointer" htmlFor={label}>
                        {description}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {loggedIn && (
              <>
                <hr />
                <button
                  className="inline-block w-full px-4 py-3 text-left hover:bg-gray-100"
                  type="button"
                  onClick={handleLogout}
                >
                  {t('auth_logout')}
                </button>
              </>
            )}
            {!loggedIn && (
              <>
                <hr />
                <div className="p-4  flex space-x-3">
                  <Button
                    variant={Button.VARIANTS.GRAY_OUTLINE}
                    className="w-full"
                    onClick={(e) => {
                      toggleMenu(e);

                      HELPERS.sendEventGA('click_login_menu', {
                        page_source: 'Header',
                      });

                      history.push(PATH.AUTH_LOGIN);
                    }}
                  >
                    {t('auth_login')}
                  </Button>
                  <Button
                    variant={Button.VARIANTS.SECONDARY}
                    className="w-full"
                    onClick={(e) => {
                      toggleMenu(e);

                      HELPERS.sendEventGA('click_register_menu', {
                        page_source: 'Header',
                      });

                      history.push(PATH.AUTH_REGISTER);
                    }}
                  >
                    {t('auth_register')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewNavbar;
